import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  ButtonSelect,
  DateAndTimePicker,
  type DateAndTimePickerFormat,
  Link as DSLink,
  Menu,
  MenuItem,
  Typography,
} from '@saviynt/design-system';
import { addYears } from 'date-fns';

import { DATE_CUSTOM_LABEL_KEY } from '../../shared/components/SodControls/CustomFilters/constants';
import { type SodCustomFilterProps } from '../../shared/components/SodControls/CustomFilters/SodCustomFilter';
import { shiftDateString } from '../../shared/components/SodControls/CustomFilters/utils';
import { CF_NEU_BLD } from '../../shared/constants';
import { useInvertedLocalize, useOutsideClick } from '../../shared/hooks';
import { OUTSIDE_CLICK_SELECTORS } from '../../shared/utils/clickOutsideUtils';

import { getDateAndTimePickerFormatForGlobalDateFormat } from './utils/utils';
import { useDateFormat } from './hooks';

import '../../shared/components/SodControls/CustomFilters/SodDateFilter.css';

const DATE_PICKER_PORTAL_ID = 'root';
const PROGRAMMATIC_OPEN_DELAY_MS = 200;

/** Selectors for elements that should not trigger outside-click close (e.g. portaled calendar). */
const TIMESTAMP_FILTER_IGNORE_SELECTORS = [
  OUTSIDE_CLICK_SELECTORS.menuDropdown,
  OUTSIDE_CLICK_SELECTORS.reactDatepicker,
] as const;

interface FilterRefType extends HTMLDivElement {
  openDropdown: () => void;
}

type AuditTimestampFilterProps = Omit<
  SodCustomFilterProps,
  'footerLink1Text' | 'handleFilterChange' | 'isDarkTheme'
>;

/**
 * Log Duration filter: date range presets plus start/end date-time pickers.
 * Menu close is handled manually so the portaled calendar does not trigger close.
 */
function AuditTimestampFilter({
  filter,
  activeOptions,
  footerLink2Text,
  enabledFilterValue,
  setEnabledFilterValue,
  handleOptionsClick,
  isDisabled,
}: AuditTimestampFilterProps): ReactElement {
  const l = useInvertedLocalize();
  const globalDateFormat = useDateFormat();
  const dateFormat: DateAndTimePickerFormat =
    getDateAndTimePickerFormatForGlobalDateFormat(globalDateFormat);

  const [isClearBtnDisabled, setIsClearBtnDisabled] = useState(true);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [optionsMap, setOptionsMap] = useState(filter.options ?? []);
  const [startDate, setStartDate] = useState<Date | undefined | null>(null);
  const [endDate, setEndDate] = useState<Date | undefined | null>(null);

  const rangeMaxDate = useMemo(() => addYears(new Date(), 10), []);
  const rangeMinDate = useMemo(() => addYears(new Date(), -10), []);

  const filterRef = useRef<FilterRefType | null>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  // Programmatic open when parent requests (e.g. from breadcrumb)
  useEffect(() => {
    const shouldOpen =
      enabledFilterValue && filter.value === enabledFilterValue;

    if (shouldOpen) {
      const timer = setTimeout(() => {
        setIsMenuExpanded(true);
        setEnabledFilterValue?.(null);
      }, PROGRAMMATIC_OPEN_DELAY_MS);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [filter.value, enabledFilterValue, setEnabledFilterValue]);

  // Outside-click close: don't close when clicking menu or portaled calendar.
  useOutsideClick({
    isEnabled: isMenuExpanded,
    containerRef: menuContainerRef,
    ignoreSelectors: TIMESTAMP_FILTER_IGNORE_SELECTORS,
    onOutsideClick: () => setIsMenuExpanded(false),
  });

  useEffect(() => {
    const isOptionTrue = optionsMap.some((option) => option.isChecked);

    if (isOptionTrue || startDate || endDate) {
      setIsClearBtnDisabled(false);

      return;
    }

    setIsClearBtnDisabled(true);
  }, [optionsMap, startDate, endDate]);

  useEffect(() => {
    // Avoid syncing from external state while date pickers are open,
    // to prevent user input from being reset mid-selection
    if (isCalendarOpen) {
      return;
    }

    // First, try to hydrate from two ISO values (current Log Duration contract)
    const isoValues = activeOptions
      .map((o) => o.value)
      .filter(
        (v): v is string =>
          typeof v === 'string' && v.includes('T') && v.endsWith('Z')
      );

    if (isoValues.length === 2) {
      const [a, b] = [...isoValues].sort();

      setStartDate(new Date(a));
      setEndDate(new Date(b));
    } else if (isoValues.length === 0) {
      // Fallback: legacy custom-date behavior using relative day shifts (if present)
      const customDate: (string | number | null)[] = JSON.parse(
        activeOptions.find((activeOption) =>
          activeOption.label.includes(DATE_CUSTOM_LABEL_KEY)
        )?.value ?? '[null, null]'
      ) as (string | number | null)[];

      const [customStartDateShift, customEndDateShift] = customDate;
      const hasLegacyShift =
        customStartDateShift !== null && customEndDateShift !== null;

      if (hasLegacyShift) {
        setStartDate(shiftDateString(Number(customStartDateShift)));
        setEndDate(shiftDateString(Number(customEndDateShift)));
      }
      // If either is missing, do not override local state to avoid resets while selecting
    }

    setOptionsMap((prev) =>
      prev.map((prevOption) => ({
        ...prevOption,
        isChecked: activeOptions.some(
          (activeOption) => activeOption.value === prevOption.value
        ),
      }))
    );
  }, [activeOptions, isCalendarOpen]);

  const handleCalendarClose = useCallback(() => setIsCalendarOpen(false), []);
  const handleCalendarOpen = useCallback(() => setIsCalendarOpen(true), []);

  const pushDurationIfComplete = useCallback(
    (start: Date | null, end: Date | null) => {
      if (!start || !end) {
        return;
      }

      const startISO = new Date(start).toISOString();
      const endISO = new Date(end).toISOString();

      handleOptionsClick(filter.value, filter.method, [
        { label: startISO, value: startISO, isChecked: true },
        { label: endISO, value: endISO, isChecked: true },
      ]);
    },
    [filter.method, filter.value, handleOptionsClick]
  );

  const handleClearDates = useCallback(() => {
    handleOptionsClick(filter.value, filter.method, []);
    setStartDate(null);
    setEndDate(null);
  }, [filter.method, filter.value, handleOptionsClick]);

  const customLabel = useMemo(() => {
    const { label } = filter;

    if (activeOptions.length !== 0) {
      const labelArray = activeOptions.map((option) => option.label).join(', ');

      return (
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          color={CF_NEU_BLD}>
          <span className='SodCustomFilter-btnLabel'>{label}</span>
          {': '}
          {labelArray}
        </Typography>
      );
    }

    return label;
  }, [activeOptions, filter]);

  return (
    <div ref={menuContainerRef}>
      <Menu
        ref={filterRef}
        isMenuExpanded={isMenuExpanded}
        setIsExpanded={setIsMenuExpanded}
        isOutsideClickEnabled={false}
        CustomMenu={
          <>
            <ul role='listbox' tabIndex={-1} className='MenuMulti-items'>
              {optionsMap.map((option) => (
                <MenuItem
                  key={option.value}
                  option={{
                    label: option.label,
                    value: option.value,
                    isChecked: option.isChecked,
                  }}
                  onClick={() => {
                    handleOptionsClick(filter.value, filter.method, [
                      {
                        label: option.label,
                        value: option.value,
                        isChecked: true,
                      },
                    ]);
                  }}
                  tabIndex={0}
                />
              ))}
              <Box tag='div' className='SodDateFilter-datePicker'>
                <DateAndTimePicker
                  kind='dateAndTimeStart'
                  label={l('Start Date')}
                  startDate={startDate}
                  setStartDate={(date: Date | null) => {
                    setStartDate(date);
                    pushDurationIfComplete(date, endDate ?? null);
                  }}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  maxDate={rangeMaxDate}
                  minDate={rangeMinDate}
                  dateFormat={dateFormat}
                  onCalendarClose={handleCalendarClose}
                  onCalendarOpen={handleCalendarOpen}
                  portalId={DATE_PICKER_PORTAL_ID}
                />
                <Typography
                  kind='body3'
                  desktop='body3-d'
                  mobile='body3-d'
                  color={CF_NEU_BLD}>
                  {l('to')}
                </Typography>
                <DateAndTimePicker
                  kind='dateAndTimeEnd'
                  label={l('End Date')}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={(date: Date | null) => {
                    setEndDate(date);
                    pushDurationIfComplete(startDate ?? null, date);
                  }}
                  maxDate={rangeMaxDate}
                  minDate={rangeMinDate}
                  dateFormat={dateFormat}
                  onCalendarClose={handleCalendarClose}
                  onCalendarOpen={handleCalendarOpen}
                  portalId={DATE_PICKER_PORTAL_ID}
                />
              </Box>
            </ul>
            <Box tag='div' className='SodRiskScoreFilter-footer'>
              <Box tag='span' aria-hidden='true' />
              <DSLink
                text={footerLink2Text}
                isDisabled={isClearBtnDisabled}
                size='small'
                kind='button'
                onClick={handleClearDates}
              />
            </Box>
          </>
        }
        label={filter.label}
        trigger={
          <ButtonSelect
            kind='pill'
            label={customLabel}
            isDisabled={isDisabled}
          />
        }
      />
    </div>
  );
}

export default memo(AuditTimestampFilter);
